/* eslint-disable jsx-a11y/anchor-is-valid */
import { Trash2 } from "lucide-react";
import { GripVertical, ArrowUpDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import * as React from "react";
import { useNavigate } from "react-router";
import { Order, compareBy, stableSort } from "../../helpers/TableHelpers";
import { ClientConfiguration } from "../../models/ClientConfiguration/ClientConfiguration";
import {UserRole} from "../../models/User";
import {convertUtcToLocal} from "../../helpers/CommonHelpers";
import { cn } from "@/lib/utils";
import { tableWrapperClassName } from "../common/Table/TableStyles";
import TableEmptyState from "../common/TableEmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import VersioningSummaryChip from "../common/VersioningSummaryChip";
import GraphLibrariesRepository from "../../repositories/GraphLibrariesRepository";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { useQuery } from "@tanstack/react-query";

interface ClientConfigurationsTableProps {
  organizationId: string;
  configurations: ClientConfiguration[];
  onDeleteConfig: (config: ClientConfiguration) => void;
  setDefaultConfigId: (id: string) => void;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const ClientConfigurationsTable: React.FC<ClientConfigurationsTableProps> = ({
  organizationId,
  configurations,
  onDeleteConfig,
  setDefaultConfigId,
  userRole,
  isAssignedUser
}) => {
  const [order, setOrder] = React.useState<Order>("desc");
  const [sort, setSort] = React.useState<string>("dateCreated");
  const navigate = useNavigate();
  const graphLibrariesRepository = new GraphLibrariesRepository();

  // Versioning status is organization-scoped, so the whole table is served by one request rather
  // than a lookup per row.
  const versioningQuery = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION_GRAPH_LIBRARY_VERSIONING, organizationId],
    queryFn: async () => await graphLibrariesRepository.getOrganizationVersioningStatus(),
  });

  const versioningByConfigurationId = React.useMemo(
    () =>
      new Map(
        (versioningQuery.data ?? []).map((configuration) => [
          configuration.clientConfigurationId,
          configuration.libraries,
        ]),
      ),
    [versioningQuery.data],
  );

  const sortedResults = sort
    ? stableSort<ClientConfiguration>(configurations, compareBy(order, sort))
    : configurations;

  const renderSortButton = (label: string, sortKey: string) => (
    <button
      type="button"
      onClick={() => {
        if (sortKey === sort) {
          setOrder(order === "asc" ? "desc" : "asc");
        } else {
          setSort(sortKey);
        }
      }}
      className={cn(
        "inline-flex cursor-pointer items-center gap-1 bg-transparent border-none p-0 font-semibold text-sm leading-none select-none",
        sort === sortKey ? "text-primary" : "text-foreground",
      )}
    >
      {label}
      <span
        className={cn(
          "inline-flex transition-transform duration-200",
          order === "asc" && sort === sortKey ? "rotate-180 -scale-x-100" : "rotate-0",
        )}
      >
        {sort === sortKey ? (
          <ArrowUpDown className="size-4" />
        ) : (
          <GripVertical className="size-4" />
        )}
      </span>
    </button>
  );

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={tableWrapperClassName}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[15%] min-w-[140px] px-1.5 py-3">
                {renderSortButton("Name", "name")}
              </TableHead>
              <TableHead className="w-[15%] min-w-[140px] px-1.5 py-3">
                Version
              </TableHead>
              <TableHead className="w-[12%] min-w-[150px] px-1.5 py-3">
                SharePoint Versioning
              </TableHead>
              <TableHead className="w-[5%] min-w-[140px] px-1.5 py-3">
                Default
              </TableHead>
              <TableHead className="w-[15%] min-w-[140px] px-1.5 py-3">
                {renderSortButton("Date Created", "dateCreated")}
              </TableHead>
              <TableHead className="w-[17.5%] min-w-[140px] px-1.5 py-3">
                Created By
              </TableHead>
              <TableHead className="w-[15%] min-w-[140px] px-1.5 py-3">
                {renderSortButton("Date Modified", "dateModified")}
              </TableHead>
              <TableHead className="w-[17.5%] min-w-[140px] px-1.5 py-3">
                Modified By
              </TableHead>
              <TableHead className="w-[50px]"/>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedResults.map((config) => {
              return (
                <TableRow
                  key={config.id}
                  className="cursor-pointer"
                  tabIndex={0}
                  onClick={() =>
                    navigate(
                      `/organization/${organizationId}/client-configuration/${config.id}`,
                    )
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(`/organization/${organizationId}/client-configuration/${config.id}`);
                    }
                  }}
                >
                  <TableCell>
                    <span className="text-sm">{config.name}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="neutral">{config.serviceVersion ? config.serviceVersion : "Latest"}</Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {/* A skeleton while loading: the chip's own "—" placeholder is also what it shows
                        for a configuration with no libraries, so the two would be indistinguishable. */}
                    {versioningQuery.isLoading ? (
                      <Skeleton className="h-5 w-24" />
                    ) : (
                      <VersioningSummaryChip libraries={versioningByConfigurationId.get(config.id!)} />
                    )}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Switch
                      disabled={userRole === UserRole.Viewer}
                      checked={config.isDefault}
                      onCheckedChange={() => setDefaultConfigId(config.id!)}
                    />
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {convertUtcToLocal(config.dateCreated as string)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{config.createdByUsername}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {
                        config.dateModified
                          ? convertUtcToLocal(config.dateModified as string)
                          : null
                      }
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{config.modifiedByUsername}</span>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      disabled={userRole === UserRole.Viewer || (!isAssignedUser && userRole !== UserRole.Admin)}
                      aria-label="Delete"
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => onDeleteConfig(config)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
              {sortedResults.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No configurations found" />
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ClientConfigurationsTable;
